import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    ColumnLayoutPreset,
    ExpressAtomicLayout,
    ExpressWidgetId,
    LayoutPage,
    MyAccountPageLayoutConfig,
    PageLayout,
    PageLayouts,
    WidgetField,
    expressWidgetLabels,
    expressZoneLabels,
    expressZoneOrder,
    getBuilderWidgetLibrary,
    getColumnLayoutOptions,
    getDefaultExpressAtomicLayout,
    getDefaultLayoutForPage,
    getExpressWidgetLibraryForZone,
    getWidgetDisplayTitle,
    isWidgetEnabled,
    layoutWidgetDefinitionsById,
    normalizeExpressAtomicLayout,
    normalizePageLayout,
} from './myAccountLayoutWidgets';

interface MyAccountLayoutBuilderProps {
    pages?: LayoutPage[];
    savedLayouts?: PageLayouts;
    scopedLayouts?: Record<string, PageLayouts>;
    scopeOptions?: LayoutScopeOption[];
    scopeFieldName?: string;
    optionFieldName?: string;
    initialPageId?: string;
    initialScopeId?: string;
    lockToPage?: boolean;
    featureFlags?: Record<string, any>;
    enabledPackages?: string[];
}

interface LayoutScopeOption {
    id: string;
    label: string;
    type?: string;
    key?: string;
}

interface DragState {
    fromColumn: number;
    fromIndex: number;
}

interface WidgetLocation {
    columnIndex: number;
    widgetIndex: number;
}

type ExpressZoneId = (typeof expressZoneOrder)[number];

const cloneLayoutConfig = <T extends MyAccountPageLayoutConfig>(layout: T): T => {
    return JSON.parse(JSON.stringify(layout)) as T;
};

const MyAccountLayoutBuilder: React.FC<MyAccountLayoutBuilderProps> = ({
    pages = [],
    savedLayouts = {},
    scopedLayouts = {},
    scopeOptions = [],
    scopeFieldName = 'weka_my_account_page_layout_scopes',
    optionFieldName = 'weka_my_account_page_layouts',
    initialPageId = '',
    initialScopeId = 'global',
    lockToPage = false,
    featureFlags = {},
    enabledPackages = [],
}) => {
    const layoutPages = Array.isArray(pages) ? pages.filter((page) => page && typeof page.id === 'string') : [];
    const normalizedScopeOptions = useMemo(() => {
        const options = Array.isArray(scopeOptions) ? scopeOptions : [];
        const normalized: LayoutScopeOption[] = [];
        const seenIds = new Set<string>();

        options.forEach((option) => {
            if (!option || typeof option.id !== 'string') {
                return;
            }
            const id = option.id.trim();
            if (id === '' || seenIds.has(id)) {
                return;
            }
            normalized.push({
                ...option,
                id,
                label: typeof option.label === 'string' && option.label.trim() !== ''
                    ? option.label.trim()
                    : id,
            });
            seenIds.add(id);
        });

        if (!seenIds.has('global')) {
            normalized.unshift({
                id: 'global',
                label: 'Global Default',
                type: 'global',
                key: '',
            });
        }

        return normalized;
    }, [scopeOptions]);

    const [selectedScopeId, setSelectedScopeId] = useState<string>(() => {
        const isInitialValid = Boolean(initialScopeId) && normalizedScopeOptions.some((scope) => scope.id === initialScopeId);
        if (isInitialValid) {
            return initialScopeId;
        }
        return normalizedScopeOptions[0]?.id || 'global';
    });

    const [scopeLayouts, setScopeLayouts] = useState<Record<string, PageLayouts>>(() => {
        const initialLayouts: Record<string, PageLayouts> = {};
        const rawScopedLayouts = scopedLayouts && typeof scopedLayouts === 'object' ? scopedLayouts : {};
        Object.keys(rawScopedLayouts).forEach((scopeId) => {
            const value = rawScopedLayouts[scopeId];
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                initialLayouts[scopeId] = value;
            }
        });

        if (!initialLayouts.global && savedLayouts && typeof savedLayouts === 'object' && !Array.isArray(savedLayouts)) {
            initialLayouts.global = savedLayouts;
        }

        normalizedScopeOptions.forEach((scope) => {
            if (!initialLayouts[scope.id]) {
                initialLayouts[scope.id] = {};
            }
        });

        if (!initialLayouts.global) {
            initialLayouts.global = {};
        }

        return initialLayouts;
    });

    const [selectedPageId, setSelectedPageId] = useState<string>(initialPageId || layoutPages[0]?.id || '');
    const [dragState, setDragState] = useState<DragState | null>(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [addToColumnIndex, setAddToColumnIndex] = useState(0);
    const [addToZoneId, setAddToZoneId] = useState<ExpressZoneId>('pre_cart');
    const [editLocation, setEditLocation] = useState<WidgetLocation | null>(null);
    const [editSettings, setEditSettings] = useState<Record<string, any>>({});
    const [titleEditLocation, setTitleEditLocation] = useState<WidgetLocation | null>(null);
    const [titleDraft, setTitleDraft] = useState('');
    const initialSerializedScopeLayoutsRef = useRef<string | null>(null);
    const initialSerializedLayoutsRef = useRef<string | null>(null);

    const selectedPage = useMemo(
        () => layoutPages.find((page) => page.id === selectedPageId),
        [layoutPages, selectedPageId]
    );

    const globalScopeLayouts: PageLayouts = useMemo(() => {
        const currentScopeLayouts = scopeLayouts.global;
        if (currentScopeLayouts && typeof currentScopeLayouts === 'object' && !Array.isArray(currentScopeLayouts)) {
            return currentScopeLayouts;
        }

        return {};
    }, [scopeLayouts]);

    const activeRawScopeLayouts: PageLayouts = useMemo(() => {
        const currentScopeLayouts = scopeLayouts[selectedScopeId];
        if (currentScopeLayouts && typeof currentScopeLayouts === 'object' && !Array.isArray(currentScopeLayouts)) {
            return currentScopeLayouts;
        }

        return {};
    }, [scopeLayouts, selectedScopeId]);

    const isGlobalScope = selectedScopeId === 'global';
    const hasSelectedPageOverride = Boolean(selectedPage && Object.prototype.hasOwnProperty.call(activeRawScopeLayouts, selectedPage.id));
    const canEditSelectedPage = Boolean(selectedPage && (isGlobalScope || hasSelectedPageOverride));

    const isExpressAtomicPage = useMemo(() => {
        if (!selectedPage) {
            return false;
        }

        return selectedPage.id === 'shopping_cart_link'
            && featureFlags?.myAccountCommerceEnabled !== false
            && featureFlags?.myAccountCommerceFlow === 'express';
    }, [featureFlags, selectedPage]);

    const effectiveSelectedPageLayout = useMemo<MyAccountPageLayoutConfig>(() => {
        if (!selectedPage) {
            return { columns: 1, columnsData: [[]] };
        }

        if (isGlobalScope) {
            return activeRawScopeLayouts[selectedPage.id] || getDefaultLayoutForPage(selectedPage);
        }

        return activeRawScopeLayouts[selectedPage.id]
            || globalScopeLayouts[selectedPage.id]
            || getDefaultLayoutForPage(selectedPage);
    }, [activeRawScopeLayouts, globalScopeLayouts, isGlobalScope, selectedPage]);

    const selectedLayout: PageLayout = useMemo(() => {
        if (!selectedPage) {
            return { columns: 1, columnsData: [[]] };
        }
        const fallback = getDefaultLayoutForPage(selectedPage);
        return normalizePageLayout(effectiveSelectedPageLayout, fallback);
    }, [effectiveSelectedPageLayout, selectedPage]);

    const selectedExpressLayout: ExpressAtomicLayout = useMemo(() => {
        if (!selectedPage) {
            return getDefaultExpressAtomicLayout();
        }

        return normalizeExpressAtomicLayout(effectiveSelectedPageLayout, getDefaultExpressAtomicLayout());
    }, [effectiveSelectedPageLayout, selectedPage]);

    useEffect(() => {
        if (!selectedPageId && layoutPages.length > 0) {
            setSelectedPageId(layoutPages[0].id);
        }
    }, [layoutPages, selectedPageId]);

    useEffect(() => {
        if (initialPageId && initialPageId !== selectedPageId) {
            setSelectedPageId(initialPageId);
        }
    }, [initialPageId, selectedPageId]);

    useEffect(() => {
        if (!normalizedScopeOptions.some((scope) => scope.id === selectedScopeId)) {
            setSelectedScopeId(normalizedScopeOptions[0]?.id || 'global');
        }
    }, [normalizedScopeOptions, selectedScopeId]);

    useEffect(() => {
        if (initialScopeId && normalizedScopeOptions.some((scope) => scope.id === initialScopeId)) {
            setSelectedScopeId(initialScopeId);
        }
    }, [initialScopeId, normalizedScopeOptions]);

    const serializedScopeLayouts = useMemo(() => JSON.stringify(scopeLayouts), [scopeLayouts]);
    const serializedLayouts = useMemo(() => JSON.stringify(scopeLayouts.global || {}), [scopeLayouts]);
    const widgetLibrary = useMemo(() => selectedPage ? getBuilderWidgetLibrary(selectedPage) : [], [selectedPage]);
    const hasPendingLayoutChanges = useMemo(() => {
        const initialScoped = initialSerializedScopeLayoutsRef.current;
        const initialGlobal = initialSerializedLayoutsRef.current;

        if (initialScoped === null || initialGlobal === null) {
            return false;
        }

        return serializedScopeLayouts !== initialScoped || serializedLayouts !== initialGlobal;
    }, [serializedLayouts, serializedScopeLayouts]);

    useEffect(() => {
        if (initialSerializedScopeLayoutsRef.current === null) {
            initialSerializedScopeLayoutsRef.current = serializedScopeLayouts;
        }

        if (initialSerializedLayoutsRef.current === null) {
            initialSerializedLayoutsRef.current = serializedLayouts;
        }
    }, [serializedLayouts, serializedScopeLayouts]);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
            return;
        }

        window.dispatchEvent(new CustomEvent('weka:layout-pending-changes', {
            detail: {
                dirty: hasPendingLayoutChanges,
            },
        }));
    }, [hasPendingLayoutChanges]);

    const ensurePageOverride = () => {
        if (!selectedPage || isGlobalScope) {
            return;
        }

        setScopeLayouts((prev) => ({
            ...prev,
            [selectedScopeId]: {
                ...(prev[selectedScopeId] || {}),
                [selectedPage.id]: cloneLayoutConfig(isExpressAtomicPage ? selectedExpressLayout : selectedLayout),
            },
        }));
    };

    const clearPageOverride = () => {
        if (!selectedPage || isGlobalScope || !hasSelectedPageOverride) {
            return;
        }

        setScopeLayouts((prev) => {
            const nextScopeLayouts = { ...(prev[selectedScopeId] || {}) };
            delete nextScopeLayouts[selectedPage.id];

            return {
                ...prev,
                [selectedScopeId]: nextScopeLayouts,
            };
        });
    };

    const updatePageLayout = (nextLayout: MyAccountPageLayoutConfig) => {
        if (!selectedPage || (!isGlobalScope && !hasSelectedPageOverride)) {
            return;
        }

        setScopeLayouts((prev) => ({
            ...prev,
            [selectedScopeId]: {
                ...(prev[selectedScopeId] || {}),
                [selectedPage.id]: nextLayout,
            },
        }));
    };

    const updateSelectedLayout = (nextLayout: PageLayout) => {
        updatePageLayout(nextLayout);
    };

    const updateSelectedExpressLayout = (nextLayout: ExpressAtomicLayout) => {
        updatePageLayout(nextLayout);
    };

    const setColumnLayout = (columnLayout: ColumnLayoutPreset) => {
        if (!canEditSelectedPage) {
            return;
        }

        const safeLayout: ColumnLayoutPreset = columnLayout === 'two_split_60_40' || columnLayout === 'two_equal'
            ? columnLayout
            : 'one';
        const safeColumns = safeLayout === 'one' ? 1 : 2;
        const nextColumnsData = Array.from({ length: safeColumns }, (_, index) => {
            return selectedLayout.columnsData[index] ? [...selectedLayout.columnsData[index]] : [];
        });
        updateSelectedLayout({
            columnLayout: safeLayout,
            columns: safeColumns,
            columnsData: nextColumnsData,
        });
    };

    const addWidget = (widgetId: string) => {
        if (!canEditSelectedPage) {
            return;
        }

        if (isExpressAtomicPage) {
            const expressWidgetId = String(widgetId || '') as ExpressWidgetId;
            const allowed = new Set(getExpressWidgetLibraryForZone(addToZoneId));
            if (!allowed.has(expressWidgetId)) {
                return;
            }

            const nextLayout: ExpressAtomicLayout = {
                ...selectedExpressLayout,
                zones: {
                    ...selectedExpressLayout.zones,
                },
            };

            const currentZoneItems = [...(nextLayout.zones[addToZoneId] || [])];
            if (!currentZoneItems.includes(expressWidgetId)) {
                currentZoneItems.push(expressWidgetId);
            }
            nextLayout.zones[addToZoneId] = currentZoneItems;
            updateSelectedExpressLayout(nextLayout);
            setIsAddModalOpen(false);
            return;
        }

        const definition = layoutWidgetDefinitionsById[widgetId];
        if (!definition) {
            return;
        }
        if (!isWidgetEnabled(definition, featureFlags, enabledPackages)) {
            return;
        }

        const nextColumns = selectedLayout.columnsData.map((column) => [...column]);
        const targetColumn = Math.max(0, Math.min(nextColumns.length - 1, addToColumnIndex));
        nextColumns[targetColumn].push({
            id: widgetId,
            settings: { ...(definition.defaultSettings || {}) },
        });

        updateSelectedLayout({
            columnLayout: selectedLayout.columnLayout,
            columns: selectedLayout.columns,
            columnsData: nextColumns,
        });

        setIsAddModalOpen(false);
    };

    const removeWidget = (columnIndex: number, widgetIndex: number) => {
        if (!canEditSelectedPage) {
            return;
        }

        const nextColumns = selectedLayout.columnsData.map((column) => [...column]);
        nextColumns[columnIndex].splice(widgetIndex, 1);
        updateSelectedLayout({
            columnLayout: selectedLayout.columnLayout,
            columns: selectedLayout.columns,
            columnsData: nextColumns,
        });
    };

    const removeExpressWidget = (zoneId: ExpressZoneId, widgetIndex: number) => {
        if (!canEditSelectedPage) {
            return;
        }

        const nextZoneItems = [...(selectedExpressLayout.zones[zoneId] || [])];
        nextZoneItems.splice(widgetIndex, 1);
        updateSelectedExpressLayout({
            ...selectedExpressLayout,
            zones: {
                ...selectedExpressLayout.zones,
                [zoneId]: nextZoneItems,
            },
        });
    };

    const moveExpressWidget = (zoneId: ExpressZoneId, widgetIndex: number, direction: -1 | 1) => {
        if (!canEditSelectedPage) {
            return;
        }

        const nextZoneItems = [...(selectedExpressLayout.zones[zoneId] || [])];
        const targetIndex = widgetIndex + direction;
        if (targetIndex < 0 || targetIndex >= nextZoneItems.length) {
            return;
        }

        const [widget] = nextZoneItems.splice(widgetIndex, 1);
        nextZoneItems.splice(targetIndex, 0, widget);
        updateSelectedExpressLayout({
            ...selectedExpressLayout,
            zones: {
                ...selectedExpressLayout.zones,
                [zoneId]: nextZoneItems,
            },
        });
    };

    const openWidgetSettings = (columnIndex: number, widgetIndex: number) => {
        if (!canEditSelectedPage) {
            return;
        }

        if (editLocation && editLocation.columnIndex === columnIndex && editLocation.widgetIndex === widgetIndex) {
            setEditLocation(null);
            return;
        }

        const widget = selectedLayout.columnsData[columnIndex]?.[widgetIndex];
        if (!widget) {
            return;
        }
        const definition = layoutWidgetDefinitionsById[widget.id];
        setTitleEditLocation(null);
        setEditLocation({ columnIndex, widgetIndex });
        setEditSettings({
            ...(definition?.defaultSettings || {}),
            ...(widget.settings || {}),
        });
    };

    const openWidgetTitleEditor = (columnIndex: number, widgetIndex: number) => {
        if (!canEditSelectedPage) {
            return;
        }

        if (titleEditLocation && titleEditLocation.columnIndex === columnIndex && titleEditLocation.widgetIndex === widgetIndex) {
            setTitleEditLocation(null);
            return;
        }

        const widget = selectedLayout.columnsData[columnIndex]?.[widgetIndex];
        if (!widget) {
            return;
        }

        const definition = layoutWidgetDefinitionsById[widget.id];
        const currentTitle = typeof widget.settings?.title === 'string' && widget.settings.title.trim() !== ''
            ? widget.settings.title.trim()
            : (definition?.defaultSettings?.title || definition?.previewTitle || '');

        setEditLocation(null);
        setTitleEditLocation({ columnIndex, widgetIndex });
        setTitleDraft(String(currentTitle || ''));
    };

    const saveWidgetSettings = () => {
        if (!editLocation) {
            return;
        }

        const nextColumns = selectedLayout.columnsData.map((column) => [...column]);
        const widget = nextColumns[editLocation.columnIndex]?.[editLocation.widgetIndex];
        if (!widget) {
            setEditLocation(null);
            return;
        }

        nextColumns[editLocation.columnIndex][editLocation.widgetIndex] = {
            ...widget,
            settings: { ...editSettings },
        };

        updateSelectedLayout({
            columnLayout: selectedLayout.columnLayout,
            columns: selectedLayout.columns,
            columnsData: nextColumns,
        });
        setEditLocation(null);
    };

    const saveWidgetTitle = () => {
        if (!titleEditLocation) {
            return;
        }

        const nextColumns = selectedLayout.columnsData.map((column) => [...column]);
        const widget = nextColumns[titleEditLocation.columnIndex]?.[titleEditLocation.widgetIndex];
        if (!widget) {
            setTitleEditLocation(null);
            return;
        }

        nextColumns[titleEditLocation.columnIndex][titleEditLocation.widgetIndex] = {
            ...widget,
            settings: {
                ...(widget.settings || {}),
                title: titleDraft.trim(),
            },
        };

        updateSelectedLayout({
            columnLayout: selectedLayout.columnLayout,
            columns: selectedLayout.columns,
            columnsData: nextColumns,
        });
        setTitleEditLocation(null);
    };

    const moveWidget = (fromColumn: number, fromIndex: number, toColumn: number, toIndex: number) => {
        if (!canEditSelectedPage) {
            return;
        }

        const nextColumns = selectedLayout.columnsData.map((column) => [...column]);
        const sourceWidget = nextColumns[fromColumn]?.[fromIndex];
        if (!sourceWidget) {
            return;
        }

        nextColumns[fromColumn].splice(fromIndex, 1);

        let targetIndex = toIndex;
        if (fromColumn === toColumn && fromIndex < toIndex) {
            targetIndex -= 1;
        }

        const boundedTarget = Math.max(0, Math.min(nextColumns[toColumn].length, targetIndex));
        nextColumns[toColumn].splice(boundedTarget, 0, sourceWidget);

        updateSelectedLayout({
            columnLayout: selectedLayout.columnLayout,
            columns: selectedLayout.columns,
            columnsData: nextColumns,
        });
    };

    const currentEditWidget = editLocation
        ? selectedLayout.columnsData[editLocation.columnIndex]?.[editLocation.widgetIndex] || null
        : null;
    const currentEditDefinition = currentEditWidget ? layoutWidgetDefinitionsById[currentEditWidget.id] : null;
    const settingsFields: WidgetField[] = (currentEditDefinition?.settingsFields || []).filter((field) => field.key !== 'title');

    const renderSettingsField = (field: WidgetField) => {
        if (field.type === 'checkbox') {
            return (
                <label key={field.key} className="weka-layout-field weka-layout-field-checkbox">
                    <input
                        type="checkbox"
                        checked={Boolean(editSettings[field.key])}
                        onChange={(event) => {
                            setEditSettings((prev) => ({
                                ...prev,
                                [field.key]: event.target.checked,
                            }));
                        }}
                    />
                    <span>{field.label}</span>
                </label>
            );
        }

        const handleValueChange = (rawValue: string) => {
            const value = field.type === 'number'
                ? (rawValue === '' ? '' : Number(rawValue))
                : rawValue;
            setEditSettings((prev) => ({
                ...prev,
                [field.key]: value,
            }));
        };

        const fieldControl = field.type === 'textarea' ? (
            <textarea
                rows={field.rows || 4}
                placeholder={field.placeholder}
                value={editSettings[field.key] ?? ''}
                onChange={(event) => handleValueChange(event.target.value)}
            />
        ) : (
            <input
                type={field.type}
                min={field.min}
                max={field.max}
                placeholder={field.placeholder}
                value={editSettings[field.key] ?? ''}
                onChange={(event) => handleValueChange(event.target.value)}
            />
        );

        return (
            <label key={field.key} className="weka-layout-field">
                <span>{field.label}</span>
                {fieldControl}
                {field.helpText && (
                    <small className="weka-layout-field-help">{field.helpText}</small>
                )}
            </label>
        );
    };

    const expressModalWidgets = isExpressAtomicPage
        ? getExpressWidgetLibraryForZone(addToZoneId).map((widgetId) => ({
            id: widgetId,
            label: expressWidgetLabels[widgetId],
            description: `Add ${expressWidgetLabels[widgetId]} to ${expressZoneLabels[addToZoneId]}.`,
            disabled: selectedExpressLayout.zones[addToZoneId]?.includes(widgetId) ?? false,
            disabledReason: 'Already added in this zone',
        }))
        : [];

    return (
        <div className="weka-layout-builder">
            <input type="hidden" name={optionFieldName} value={serializedLayouts} />
            {scopeFieldName && (
                <input type="hidden" name={scopeFieldName} value={serializedScopeLayouts} />
            )}

            <div className="weka-layout-toolbar">
                <label>
                    <span>Layout Scope</span>
                    <select
                        value={selectedScopeId}
                        onChange={(event) => setSelectedScopeId(event.target.value)}
                    >
                        {normalizedScopeOptions.map((scope) => (
                            <option key={scope.id} value={scope.id}>
                                {scope.label}
                            </option>
                        ))}
                    </select>
                </label>

                {!lockToPage && (
                    <label>
                        <span>Page</span>
                        <select
                            value={selectedPageId}
                            onChange={(event) => setSelectedPageId(event.target.value)}
                        >
                            {layoutPages.map((page) => (
                                <option key={page.id} value={page.id}>
                                    {page.label}
                                </option>
                            ))}
                        </select>
                    </label>
                )}

                {isExpressAtomicPage ? (
                    <div className="weka-layout-mode-note">
                        <strong>Express Atomic Layout</strong>
                        <span>Checkout shell is required and managed by B2B Checkout.</span>
                    </div>
                ) : (
                    <label>
                        <span>Layout</span>
                        <div className="weka-layout-presets" role="group" aria-label="Column layout presets">
                            {getColumnLayoutOptions().map((option) => {
                                const active = (selectedLayout.columnLayout || 'one') === option.id;
                                return (
                                    <button
                                        key={option.id}
                                        type="button"
                                        className={`weka-layout-preset ${active ? 'is-active' : ''}`}
                                        onClick={() => setColumnLayout(option.id)}
                                    >
                                        <span className={`weka-layout-preset-visual ${option.id}`}>
                                            {option.id === 'one' && <span className="v v-full" />}
                                            {option.id === 'two_equal' && (<><span className="v v-half" /><span className="v v-half" /></>)}
                                            {option.id === 'two_split_60_40' && (<><span className="v v-sixty" /><span className="v v-forty" /></>)}
                                        </span>
                                        <span className="weka-layout-preset-label">{option.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </label>
                )}
            </div>

            {hasPendingLayoutChanges && (
                <div className="weka-layout-pending-notice" role="status">
                    <strong>Pending changes</strong>
                    <span>These layout updates are staged only. Click Save Settings on the parent page to persist them.</span>
                </div>
            )}

            {!isGlobalScope && selectedPage && (
                <div className={`weka-layout-inheritance-panel ${hasSelectedPageOverride ? 'is-overridden' : 'is-inherited'}`}>
                    <div>
                        <strong>{hasSelectedPageOverride ? 'Role override enabled' : 'Using global layout'}</strong>
                        <span>
                            {hasSelectedPageOverride
                                ? 'This page layout is stored only for the selected role.'
                                : 'This role currently inherits the global layout for this page.'}
                        </span>
                    </div>
                    <div className="weka-layout-inheritance-actions">
                        {!hasSelectedPageOverride && (
                            <button type="button" className="button weka-primary-btn" onClick={ensurePageOverride}>
                                Override for this role
                            </button>
                        )}
                        {hasSelectedPageOverride && (
                            <button type="button" className="button" onClick={clearPageOverride}>
                                Use global layout
                            </button>
                        )}
                    </div>
                </div>
            )}

            {isExpressAtomicPage ? (
                <div className="weka-layout-express-zones">
                    {expressZoneOrder.map((zoneId) => {
                        const zoneWidgets = selectedExpressLayout.zones[zoneId] || [];
                        return (
                            <div key={zoneId} className="weka-layout-zone">
                                <div className="weka-layout-zone-header">
                                    <div>
                                        <strong>{expressZoneLabels[zoneId]}</strong>
                                        <span className="weka-layout-zone-hint">{zoneId}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="button weka-secondary-btn"
                                        onClick={() => {
                                            setAddToZoneId(zoneId);
                                            setIsAddModalOpen(true);
                                        }}
                                        disabled={!canEditSelectedPage}
                                    >
                                        Add Widget
                                    </button>
                                </div>
                                <div className="weka-layout-zone-body">
                                    {zoneWidgets.length === 0 && (
                                        <div className="weka-layout-empty">No widgets in this zone.</div>
                                    )}
                                    {zoneWidgets.map((widgetId, widgetIndex) => (
                                        <div key={`${zoneId}-${widgetId}-${widgetIndex}`} className="weka-layout-widget">
                                            <div className="weka-layout-widget-title">
                                                <strong>{expressWidgetLabels[widgetId]}</strong>
                                            </div>
                                            <div className="weka-layout-widget-actions">
                                                <button
                                                    type="button"
                                                    className="button button-small"
                                                    onClick={() => moveExpressWidget(zoneId, widgetIndex, -1)}
                                                    disabled={widgetIndex === 0}
                                                >
                                                    Up
                                                </button>
                                                <button
                                                    type="button"
                                                    className="button button-small"
                                                    onClick={() => moveExpressWidget(zoneId, widgetIndex, 1)}
                                                    disabled={widgetIndex === zoneWidgets.length - 1}
                                                >
                                                    Down
                                                </button>
                                                <button
                                                    type="button"
                                                    className="button button-small"
                                                    onClick={() => removeExpressWidget(zoneId, widgetIndex)}
                                                    disabled={!canEditSelectedPage}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className={`weka-layout-canvas layout-${selectedLayout.columnLayout || 'one'}`}>
                    {selectedLayout.columnsData.map((column, columnIndex) => (
                        <div
                            key={`column-${columnIndex}`}
                            className="weka-layout-column"
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={(event) => {
                                event.preventDefault();
                                if (!dragState) {
                                    return;
                                }
                                moveWidget(dragState.fromColumn, dragState.fromIndex, columnIndex, column.length);
                                setDragState(null);
                            }}
                        >
                            <div className="weka-layout-column-header">
                                <strong>Column {columnIndex + 1}</strong>
                                <button
                                    type="button"
                                    className="button weka-secondary-btn"
                                    onClick={() => {
                                        setAddToColumnIndex(columnIndex);
                                        setIsAddModalOpen(true);
                                    }}
                                    disabled={!canEditSelectedPage}
                                >
                                    Add Widget
                                </button>
                            </div>

                            <div className="weka-layout-column-body">
                                {column.length === 0 && (
                                    <div className="weka-layout-empty">
                                        Drop widgets here
                                    </div>
                                )}

                                {column.map((widget, widgetIndex) => {
                                    const definition = layoutWidgetDefinitionsById[widget.id];
                                    if (!definition) {
                                        return null;
                                    }

                                    const canEdit = Boolean(definition.supportsSettings);
                                    const canRename = Boolean(definition.settingsFields?.some((field) => field.key === 'title'));
                                    const enabled = isWidgetEnabled(widget, featureFlags, enabledPackages);
                                    const isEditing = Boolean(
                                        editLocation
                                        && editLocation.columnIndex === columnIndex
                                        && editLocation.widgetIndex === widgetIndex
                                    );
                                    const isRenaming = Boolean(
                                        titleEditLocation
                                        && titleEditLocation.columnIndex === columnIndex
                                        && titleEditLocation.widgetIndex === widgetIndex
                                    );
                                    return (
                                        <div
                                            key={`widget-${columnIndex}-${widgetIndex}`}
                                            className={`weka-layout-widget ${enabled ? '' : 'is-disabled'} ${isEditing || isRenaming ? 'is-editing' : ''}`}
                                            draggable={enabled && canEditSelectedPage && !isEditing && !isRenaming}
                                            onDragStart={() => enabled && canEditSelectedPage && setDragState({ fromColumn: columnIndex, fromIndex: widgetIndex })}
                                            onDragEnd={() => setDragState(null)}
                                            onDragOver={(event) => event.preventDefault()}
                                            onDrop={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                if (!dragState) {
                                                    return;
                                                }
                                                moveWidget(dragState.fromColumn, dragState.fromIndex, columnIndex, widgetIndex);
                                                setDragState(null);
                                            }}
                                        >
                                            <div className="weka-layout-widget-title">
                                                <span className="dashicons dashicons-move" />
                                                <strong>{getWidgetDisplayTitle(widget)}</strong>
                                                {canRename && (
                                                    <button
                                                        type="button"
                                                        className="button-link weka-layout-rename-button"
                                                        onClick={() => openWidgetTitleEditor(columnIndex, widgetIndex)}
                                                        disabled={!canEditSelectedPage}
                                                        aria-label={`Rename ${getWidgetDisplayTitle(widget)}`}
                                                        title="Rename widget"
                                                    >
                                                        <span className="dashicons dashicons-edit" />
                                                    </button>
                                                )}
                                                {!enabled && <em className="weka-layout-disabled-badge">Disabled</em>}
                                            </div>
                                            <div className="weka-layout-widget-actions">
                                                {canEdit && (
                                                    <button
                                                        type="button"
                                                        className="button button-small"
                                                        onClick={() => openWidgetSettings(columnIndex, widgetIndex)}
                                                        disabled={!canEditSelectedPage}
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="button button-small"
                                                    onClick={() => removeWidget(columnIndex, widgetIndex)}
                                                    disabled={!canEditSelectedPage}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                            {isRenaming && (
                                                <div className="weka-layout-inline-editor weka-layout-inline-editor-compact">
                                                    <div className="weka-layout-inline-editor-header">
                                                        <strong>Rename Widget</strong>
                                                        <span>Apply updates here, then use Save Settings on the parent page to persist them.</span>
                                                    </div>
                                                    <label className="weka-layout-field">
                                                        <span>Widget Title</span>
                                                        <input
                                                            type="text"
                                                            value={titleDraft}
                                                            onChange={(event) => setTitleDraft(event.target.value)}
                                                            placeholder={definition.defaultSettings?.title || definition.previewTitle}
                                                        />
                                                    </label>
                                                    <div className="weka-layout-modal-actions">
                                                        <button type="button" className="button weka-primary-btn" onClick={saveWidgetTitle}>
                                                            Apply
                                                        </button>
                                                        <button type="button" className="button" onClick={() => setTitleEditLocation(null)}>
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            {isEditing && (
                                                <div className="weka-layout-inline-editor">
                                                    <div className="weka-layout-inline-editor-header">
                                                        <strong>{currentEditDefinition?.label || definition.label} Settings</strong>
                                                        <span>Apply updates here, then use Save Settings on the parent page to persist them.</span>
                                                    </div>
                                                    <div className="weka-layout-form">
                                                        {settingsFields.map(renderSettingsField)}
                                                    </div>
                                                    <div className="weka-layout-modal-actions">
                                                        <button type="button" className="button weka-primary-btn" onClick={saveWidgetSettings}>
                                                            Apply
                                                        </button>
                                                        <button type="button" className="button" onClick={() => setEditLocation(null)}>
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isAddModalOpen && (
                <div className="weka-layout-modal-overlay">
                    <div className="weka-layout-modal">
                        <h3>Add Widget</h3>
                        <div className="weka-layout-widget-grid">
                            {isExpressAtomicPage
                                ? expressModalWidgets.map((widget) => (
                                    <button
                                        key={widget.id}
                                        type="button"
                                        className="weka-layout-widget-card"
                                        disabled={widget.disabled}
                                        onClick={() => addWidget(widget.id)}
                                    >
                                        <strong>{widget.label}</strong>
                                        <span>{widget.description}</span>
                                        {widget.disabled && (
                                            <span className="weka-layout-widget-disabled-note">{widget.disabledReason}</span>
                                        )}
                                    </button>
                                ))
                                : widgetLibrary.map((widget) => (
                                    <button
                                        key={widget.id}
                                        type="button"
                                        className="weka-layout-widget-card"
                                        disabled={!isWidgetEnabled(widget, featureFlags, enabledPackages)}
                                        onClick={() => addWidget(widget.id)}
                                    >
                                        <strong>{widget.label}</strong>
                                        <span>{widget.description}</span>
                                        {!isWidgetEnabled(widget, featureFlags, enabledPackages) && (
                                            <span className="weka-layout-widget-disabled-note">Disabled by package/feature toggle</span>
                                        )}
                                    </button>
                                ))}
                        </div>
                        <div className="weka-layout-modal-actions">
                            <button type="button" className="button" onClick={() => setIsAddModalOpen(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .weka-layout-builder { margin-top: 10px; }
                .weka-layout-toolbar { display: flex; gap: 16px; align-items: end; margin-bottom: 16px; flex-wrap: wrap; }
                .weka-layout-toolbar label { display: flex; flex-direction: column; gap: 6px; }
                .weka-layout-toolbar select { min-width: 200px; }
                .weka-layout-mode-note { border: 1px solid #c3c4c7; border-radius: 6px; background: #fff; padding: 10px 12px; display: flex; flex-direction: column; gap: 4px; min-width: 260px; }
                .weka-layout-mode-note span { color: #646970; font-size: 12px; }
                .weka-layout-inheritance-panel { border: 1px solid #dcdcde; border-radius: 6px; background: #fff; margin-bottom: 16px; padding: 12px; display: flex; gap: 12px; align-items: center; justify-content: space-between; }
                .weka-layout-inheritance-panel strong { display: block; }
                .weka-layout-inheritance-panel span { display: block; color: #646970; font-size: 12px; margin-top: 4px; }
                .weka-layout-inheritance-panel.is-inherited { background: #f6f7f7; }
                .weka-layout-inheritance-actions { display: flex; gap: 8px; align-items: center; }
                .weka-layout-pending-notice { border: 1px solid #d63638; border-left-width: 4px; border-radius: 6px; background: #fcf0f1; color: #8a2424; padding: 12px 14px; display: grid; gap: 4px; margin-bottom: 16px; }
                .weka-layout-pending-notice span { font-size: 12px; line-height: 1.4; }
                .weka-layout-canvas { display: grid; gap: 12px; }
                .weka-layout-canvas.layout-one { grid-template-columns: 1fr; }
                .weka-layout-canvas.layout-two_equal { grid-template-columns: repeat(2, 1fr); }
                .weka-layout-canvas.layout-two_split_60_40 { grid-template-columns: 3fr 2fr; }
                .weka-layout-presets { display: flex; gap: 8px; align-items: stretch; flex-wrap: wrap; }
                .weka-layout-preset { min-width: 170px; border: 1px solid #c3c4c7; background: #fff; border-radius: 6px; padding: 8px; display: flex; flex-direction: column; gap: 8px; cursor: pointer; text-align: left; }
                .weka-layout-preset.is-active { border-color: #2271b1; box-shadow: 0 0 0 1px #2271b1; }
                .weka-layout-preset-visual { height: 32px; border: 1px solid #dcdcde; border-radius: 4px; background: #f6f7f7; display: flex; gap: 4px; padding: 4px; }
                .weka-layout-preset-visual .v { display: block; border-radius: 2px; background: #c3c4c7; }
                .weka-layout-preset-visual .v-full { width: 100%; }
                .weka-layout-preset-visual .v-half { width: calc(50% - 2px); }
                .weka-layout-preset-visual .v-sixty { width: calc(60% - 2px); }
                .weka-layout-preset-visual .v-forty { width: calc(40% - 2px); }
                .weka-layout-preset-label { font-size: 12px; color: #1d2327; line-height: 1.2; }
                .weka-layout-column,
                .weka-layout-zone { background: #fff; border: 1px solid #dcdcde; border-radius: 6px; min-height: 220px; display: flex; flex-direction: column; }
                .weka-layout-column-header,
                .weka-layout-zone-header { padding: 12px; border-bottom: 1px solid #f0f0f1; display: flex; justify-content: space-between; align-items: center; gap: 10px; }
                .weka-layout-zone-header strong { display: block; }
                .weka-layout-zone-hint { display: block; color: #646970; font-size: 11px; margin-top: 2px; }
                .weka-layout-column-body,
                .weka-layout-zone-body { padding: 12px; display: flex; flex-direction: column; gap: 10px; flex: 1; }
                .weka-layout-empty { border: 1px dashed #c3c4c7; border-radius: 4px; padding: 24px; text-align: center; color: #646970; }
                .weka-layout-widget { border: 1px solid #c3c4c7; border-radius: 4px; background: #f6f7f7; padding: 10px; display: flex; justify-content: space-between; gap: 12px; align-items: center; cursor: move; flex-wrap: wrap; }
                .weka-layout-widget.is-disabled { opacity: .55; cursor: not-allowed; }
                .weka-layout-widget.is-editing { cursor: default; background: #fff; border-color: #2271b1; box-shadow: 0 0 0 1px rgba(34, 113, 177, 0.15); }
                .weka-layout-widget-title { display: flex; align-items: center; gap: 8px; }
                .weka-layout-rename-button { display: inline-flex; align-items: center; justify-content: center; color: #50575e; text-decoration: none; padding: 0; min-width: auto; }
                .weka-layout-rename-button .dashicons { font-size: 16px; width: 16px; height: 16px; }
                .weka-layout-disabled-badge { font-style: normal; font-size: 11px; color: #b32d2e; background: #fbeaea; border: 1px solid #f0bcbc; padding: 1px 6px; border-radius: 999px; }
                .weka-layout-widget-actions { display: flex; gap: 6px; }
                .weka-layout-inline-editor { width: 100%; margin-top: 4px; padding-top: 12px; border-top: 1px solid #dcdcde; display: grid; gap: 12px; }
                .weka-layout-inline-editor-compact { gap: 10px; }
                .weka-layout-inline-editor-header { display: grid; gap: 4px; }
                .weka-layout-inline-editor-header span { color: #646970; font-size: 12px; }
                .weka-layout-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 100000; display: flex; align-items: center; justify-content: center; padding: 24px; }
                .weka-layout-modal { background: #fff; width: 100%; max-width: 680px; border-radius: 8px; padding: 18px; max-height: calc(100vh - 48px); overflow: auto; }
                .weka-layout-widget-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 10px; margin-top: 12px; }
                .weka-layout-widget-card { text-align: left; border: 1px solid #c3c4c7; border-radius: 6px; background: #f6f7f7; padding: 12px; display: flex; flex-direction: column; gap: 6px; cursor: pointer; }
                .weka-layout-widget-card span { color: #50575e; font-size: 12px; }
                .weka-layout-widget-card:disabled { opacity: .55; cursor: not-allowed; }
                .weka-layout-widget-disabled-note { color: #b32d2e !important; }
                .weka-layout-modal-actions { margin-top: 14px; display: flex; gap: 8px; justify-content: flex-end; }
                .weka-layout-form { display: grid; gap: 10px; margin-top: 10px; }
                .weka-layout-field { display: flex; flex-direction: column; gap: 6px; }
                .weka-layout-field:not(.weka-layout-field-checkbox) input,
                .weka-layout-field textarea { width: 100%; }
                .weka-layout-field-checkbox { flex-direction: row; align-items: flex-start; gap: 10px; }
                .weka-layout-field-checkbox input[type='checkbox'] { width: auto; height: auto; margin: 3px 0 0; flex: 0 0 auto; }
                .weka-layout-field-help { color: #646970; line-height: 1.4; }
                .weka-layout-express-zones { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); }
                @media (max-width: 980px) {
                    .weka-layout-inheritance-panel { flex-direction: column; align-items: stretch; }
                    .weka-layout-canvas.layout-two_equal,
                    .weka-layout-canvas.layout-two_split_60_40 { grid-template-columns: 1fr; }
                }
            `}</style>
        </div>
    );
};

export default MyAccountLayoutBuilder;
